import {BaseView} from "./BaseView";
import {useSearchParams} from "react-router-dom";
import React, {useEffect} from "react";
import axios from "../utility/customAxios";
import {useSelector} from "react-redux";
import timerange from "../store/reducers/timerange";
import {GanttChart} from "../components/Gantt/GanttChart";
import {MetricType} from "./MetricsTest";
import {BellOffIcon, AlertTriangleIcon} from "lucide-react";
import {useDebouncedCallback} from "use-debounce";
import {
    AggregateMonitorEvaluationPayload,
    AggregationFunction,
    EvalType,
    MonitorEvaluation,
} from "./alerts/MetricAlert";
import {DateTimePrecision, getStartOfBucket} from "../utility/displayDateTime";
import {getGraphAnnotationProperties, getWindowUnitSeconds} from "../components/Alert/AlertMetricSelector";
import {ChartType} from "../components/Charts/MetoroChart";
import {MultiMetoroMetricsChart, MetricSpecifier} from "../components/Charts/MultiMetoroMetricsCharts";
import {ApiServerAlert} from "../components/Alert/utils";
import {AlertDetails} from "../components/Alert/AlertDetails";
import {AlertFire, AlertFiringHistoryList} from "../components/Alert/AlertFiringHistoryList";
import {TimeRange} from "../types/time";
import {getAggregateStartEndTime, getMetoroQLQueryStartEndTime, StatChart} from "../components/Charts/StatChart";
import {ThresholdComparator} from "./alerts/model";
import {ReduceOption} from "../components/Dashboarding/internalwidgets";
import {round} from "../components/Alert/AlertMultiMetricSelector";
import { withErrorHandling } from "../utility/apiUtils";

var hdate = require('human-date')

function updateAlert(alertId: string, setDoesNotHavePermission: (value: boolean) => void, setAlert: (value: ApiServerAlert | undefined) => void): void {
    withErrorHandling(async () => {
        const res = await axios.get<ApiServerAlert>(`/api/v1/alert?alertId=${alertId}`);
        const alert = res.data;

        // Convert filter objects to Maps
        if (alert.multiMetricAlert) {
            alert.multiMetricAlert.filters.metricSpecifiers = alert.multiMetricAlert.filters.metricSpecifiers.map((spec: MetricSpecifier) => ({
                ...spec,
                filters: new Map(Object.entries(spec.filters || {})),
                excludeFilters: new Map(Object.entries(spec.excludeFilters || {}))
            }));
        }

        setAlert(alert);
        return res.data;
    }).catch((error) => {
        console.error(error);
        setDoesNotHavePermission(true);
        return undefined;
    });
}

function extractMonitorEvaluationObj(alert: ApiServerAlert) {
    let monitorEvaluation: MonitorEvaluation | undefined;
    if (alert.type === "Metric" && alert.metricAlert) {
        monitorEvaluation = alert.metricAlert.monitorEvaluation;
    } else if (alert.type === "Trace" && alert.traceAlert) {
        monitorEvaluation = alert.traceAlert.monitorEvaluation;
    } else if (alert.type === "KubernetesResource" && alert.kubernetesResourceAlert) {
        monitorEvaluation = alert.kubernetesResourceAlert.monitorEvaluation;
    } else if (alert.type === "MultiMetric" && alert.multiMetricAlert) {
        monitorEvaluation = alert.multiMetricAlert.monitorEvaluation;
    }

    if (monitorEvaluation) {
        return monitorEvaluation;
    }

    return {
        monitorEvaluationType: EvalType.Static,
        description: "",
        monitorEvalutionPayload: {
            evaluationWindow: 1,
            datapointsToAlarm: 1
        }
    } as MonitorEvaluation;
}

function alertFiresToGanttData(alertFires: AlertFire[], startTime: Date, endTime: Date) {
    let cells = alertFires.map((alertFire) => {
        // Use datapoint breach time if available and non-zero, otherwise fall back to alert start time
        const startTimeToUse = alertFire.properties?.datapointBreaching?.time && alertFire.properties.datapointBreaching.time !== 0
            ? new Date(alertFire.properties.datapointBreaching.time) // Convert from Unix timestamp
            : new Date(alertFire.startTime * 1000);

        return {
            startTime: startTimeToUse,
            endTime: alertFire.endTime === null ? new Date() : new Date(alertFire.endTime * 1000),
            text: "Alert",
            color: "rgb(239 68 68)",
            onClick: () => {
            },
        }
    });
    const startTimeAdjustedForBucket = new Date(getStartOfBucket(startTime.getTime(), DateTimePrecision.Minutes));
    let baseline = {
        startTime: startTimeAdjustedForBucket, endTime: endTime, text: "Ok", color: "rgb(34 197 94)", onClick: () => {
        }
    };
    cells.push(baseline);

    return {
        barThickness: 20,
        tooltipCallback: (tooltipItem: any) => {
            return hdate.prettyPrint(new Date(tooltipItem.raw[0]), {showTime: true}) + " - " + hdate.prettyPrint(new Date(tooltipItem.raw[1]), {showTime: true})
        },
        startTime: startTimeAdjustedForBucket,
        endTime: endTime,
        rows: [
            {
                label: "Status",
                cells: cells
            }
        ]
    }
}

function updateAlertFires(timeRange: TimeRange, alertId: string | null, setAlertFires: (value: (((prevState: AlertFire[]) => AlertFire[]) | AlertFire[])) => void) {
    // unix seconds
    const start = Math.floor(timeRange.getStartEnd()[0].getTime()! / 1000);
    const end = Math.floor(timeRange.getStartEnd()[1].getTime()! / 1000);

    withErrorHandling(async () => {
        const res = await axios.get(`/api/v1/alertFires?alertId=${alertId}&startTime=${start}&endTime=${end}`);
        setAlertFires(res.data.alertFires);
        return res.data;
    }).catch((error) => {
        console.error(error);
        setAlertFires([]);
    });
}

function Alert() {
    const [searchParams, setSearchParams] = useSearchParams();
    const alertId = searchParams.get("alertId");
    const [alert, setAlert] = React.useState<ApiServerAlert>();
    const [alertFires, setAlertFires] = React.useState<AlertFire[]>([]);
    const timeRange = useSelector(timerange.selectors.getTimeRange)
    const debouncedUpdateAlertFires = useDebouncedCallback(updateAlertFires, 10);
    const debouncedUpdateAlert = useDebouncedCallback(updateAlert, 10);
    const [doesNotHavePermission, setDoesNotHavePermission] = React.useState(false);
    useEffect(() => {
        debouncedUpdateAlertFires(timeRange, alertId, setAlertFires);
    }, [alertId, timeRange]);


    useEffect(() => {
        if (alertId) {
            debouncedUpdateAlert(alertId, setDoesNotHavePermission, setAlert);
        }
    }, [alertId]);

    let alertEvaluationType = EvalType.Static
    let alertEvaluationFunction = AggregationFunction.Sum
    let annotationProps;
    if (alert && alert.type == "Metric" && alert.metricAlert) {
        const monitorEvaluationObj = extractMonitorEvaluationObj(alert);
        annotationProps = getGraphAnnotationProperties(
            monitorEvaluationObj,
            timeRange,
            parseFloat(alert.metricAlert.alarmCondition.threshold),
            alert.metricAlert.alarmCondition.condition
        )
        alertEvaluationType = alert.metricAlert.monitorEvaluation.monitorEvaluationType
        if (monitorEvaluationObj.monitorEvaluationType === EvalType.Aggregate) {
            alertEvaluationFunction = alert.metricAlert.monitorEvaluation.monitorEvalutionPayload.evaluationFunction
        }
    } else if (alert && alert.type == "Trace" && alert.traceAlert) {
        const monitorEvaluationObj = extractMonitorEvaluationObj(alert)
        annotationProps = getGraphAnnotationProperties(
            monitorEvaluationObj,
            timeRange,
            parseFloat(alert.traceAlert.alarmCondition.threshold),
            alert.traceAlert.alarmCondition.condition
        )
        alertEvaluationType = alert.traceAlert.monitorEvaluation.monitorEvaluationType
        if (monitorEvaluationObj.monitorEvaluationType === EvalType.Aggregate) {
            alertEvaluationFunction = alert.traceAlert.monitorEvaluation.monitorEvalutionPayload.evaluationFunction
        }
    } else {
        annotationProps = {thresholdToSet: 0, timePeriodToHighlight: [], annotationLabel: ""}
    }

    let typedAlert;
    if (alert && alert.type == "MultiMetric" && alert.multiMetricAlert) {
        typedAlert = alert.multiMetricAlert;
    }

    // Check if alert is managed and last synced time is over 6 hours ago or null
    const isOutOfSync = alert && 
        alert.alertManagement && 
        alert.alertManagement.managingResourceType && 
        (!alert.alertManagement.lastSyncedTime || 
        (Date.now() / 1000 - alert.alertManagement.lastSyncedTime) > 6 * 60 * 60);

    return <BaseView title={"Alert"} disableClusterSelector={true}>
        <div className={"flex flex-col overflow-y-auto gap-2 m-2 grow shrink min-h-0 min-w-0"}>
            {alert && alert.muted &&
                <div
                    className={"flex items-center justify-center gap-2 py-2 text-lg border bg-amber-500/10 border-amber-500 rounded text-amber-500"}>
                    <BellOffIcon className={"w-5 h-6"}/>
                    Muted -
                    <div className={"text-base"}> You will not get notified when this alert fires. </div>
                </div>}
            {isOutOfSync &&
                <div
                    className={"flex items-center justify-center gap-2 py-2 text-lg border bg-red-500/10 border-red-500 rounded text-red-500"}>
                    <AlertTriangleIcon className={"w-5 h-6"}/>
                    <div className={"text-base"}> Out of Sync - This managed alert hasn't been synced in over 6 hours. </div>
                </div>}
            <AlertDetails alert={alert} setAlert={setAlert} doesNotHavePermission={doesNotHavePermission}/>
            {alert && alert.type == "Metric" && alert.metricAlert &&
                <div className={"flex flex-col"}>
                    <div className={"flex relative h-[300px]"}>
                        <MultiMetoroMetricsChart
                            className={"flex flex-grow"}
                            startTime={Math.floor(timeRange.getStartEnd()[0].getTime() / 1000)}
                            endTime={Math.floor((timeRange.getStartEnd()[1].getTime()) / 1000)}
                            type={ChartType.Line}
                            title={`${alert.metricAlert.filters.metricName}-${alert.metricAlert.filters.aggregation}`}
                            threshold={getGraphAnnotationProperties(extractMonitorEvaluationObj(alert), timeRange, parseFloat(alert.metricAlert.alarmCondition.threshold), alert.metricAlert.alarmCondition.condition).thresholdToSet}
                            thresholdLabel={annotationProps.annotationLabel}
                            timePeriodHighlight={getGraphAnnotationProperties(extractMonitorEvaluationObj(alert), timeRange, parseFloat(alert.metricAlert.alarmCondition.threshold), alert.metricAlert.alarmCondition.condition).timePeriodToHighlight}
                            hideLegend={false}
                            metricSpecifiers={[{
                                metricName: alert.metricAlert.filters.metricName,
                                aggregation: alert.metricAlert.filters.aggregation,
                                bucketSize: alert.metricAlert.filters.bucketSize,
                                filters: new Map(Object.entries(alert.metricAlert.filters.filters)),
                                excludeFilters: new Map(Object.entries(alert.metricAlert.filters.excludeFilters || {})),
                                splits: alert.metricAlert.filters.splits,
                                metricType: MetricType.Metric,
                                functions: alert.metricAlert.filters.functions,
                            } as MetricSpecifier]}
                        />
                    </div>
                </div>}
            {alert && alert.type == "Trace" && alert.traceAlert &&
                <div className={"flex flex-col"}>
                    <div className={"flex relative h-[300px]"}>
                        <MultiMetoroMetricsChart
                            className={"flex flex-grow"}
                            startTime={Math.floor(timeRange.getStartEnd()[0].getTime() / 1000)}
                            endTime={Math.floor((timeRange.getStartEnd()[1].getTime()) / 1000)}
                            type={ChartType.Line}
                            title={`${alert.traceAlert.filters.aggregation}`}
                            threshold={getGraphAnnotationProperties(extractMonitorEvaluationObj(alert), timeRange, parseFloat(alert.traceAlert.alarmCondition.threshold), alert.traceAlert.alarmCondition.condition).thresholdToSet}
                            thresholdLabel={annotationProps.annotationLabel}
                            timePeriodHighlight={getGraphAnnotationProperties(extractMonitorEvaluationObj(alert), timeRange, parseFloat(alert.traceAlert.alarmCondition.threshold), alert.traceAlert.alarmCondition.condition).timePeriodToHighlight}
                            hideLegend={false}
                            metricSpecifiers={[{
                                metricName: "",
                                aggregation: alert.traceAlert.filters.aggregation,
                                bucketSize: alert.traceAlert.filters.bucketSize,
                                filters: new Map(Object.entries(alert.traceAlert.filters.filters)),
                                excludeFilters: new Map(Object.entries(alert.traceAlert.filters.excludeFilters || {})),
                                splits: alert.traceAlert.filters.splits,
                                metricType: MetricType.Trace,
                                functions: alert.traceAlert.filters.functions,
                            } as MetricSpecifier]}
                        />
                    </div>
                </div>}
            {alert && alert.type == "KubernetesResource" && alert.kubernetesResourceAlert &&
                <div className={"flex flex-col"}>
                    <div className={"flex relative h-[300px]"}>
                        <MultiMetoroMetricsChart
                            className={"flex flex-grow"}
                            startTime={Math.floor(timeRange.getStartEnd()[0].getTime() / 1000)}
                            endTime={Math.floor((timeRange.getStartEnd()[1].getTime()) / 1000)}
                            metricSpecifiers={
                                [
                                    {
                                        metricName: "Kubernetes Resource",
                                        aggregation: alert.kubernetesResourceAlert.filters.aggregation,
                                        bucketSize: alert.kubernetesResourceAlert.filters.bucketSize,
                                        filters: new Map(Object.entries(alert.kubernetesResourceAlert.filters.filters)),
                                        excludeFilters: new Map(Object.entries(alert.kubernetesResourceAlert.filters.excludeFilters || {})),
                                        splits: alert.kubernetesResourceAlert.filters.splits,
                                        functions: alert.kubernetesResourceAlert.filters.functions,
                                        metricType: MetricType.Kubernetes,
                                        jsonPath: alert.kubernetesResourceAlert.filters.jsonPath
                                    }
                                ]
                            }
                            type={ChartType.Line}
                            title={`${alert.kubernetesResourceAlert.filters.aggregation}`}
                            threshold={getGraphAnnotationProperties(extractMonitorEvaluationObj(alert), timeRange, parseFloat(alert.kubernetesResourceAlert.alarmCondition.threshold), alert.kubernetesResourceAlert.alarmCondition.condition).thresholdToSet}
                            thresholdLabel={annotationProps.annotationLabel}
                            timePeriodHighlight={getGraphAnnotationProperties(extractMonitorEvaluationObj(alert), timeRange, parseFloat(alert.kubernetesResourceAlert.alarmCondition.threshold), alert.kubernetesResourceAlert.alarmCondition.condition).timePeriodToHighlight}
                            hideLegend={false}
                        />
                    </div>
                </div>}
            {alert && alert.type == "MultiMetric" && alert.multiMetricAlert && typedAlert && typedAlert.monitorEvaluation.monitorEvaluationType == EvalType.Aggregate &&
                <div className={"grid grid-cols-2 gap-4"}>
                    <div className={"flex flex-col justify-center h-[300px]"}>
                        <StatChart
                            isSelecting={true}
                            mappings={
                                [
                                    {
                                        value: parseFloat(typedAlert.alarmCondition.threshold),
                                        operator: (() => {
                                            switch (typedAlert.alarmCondition.condition) {
                                                case ThresholdComparator.GreaterThan:
                                                    return ">"
                                                case ThresholdComparator.LessThan:
                                                    return "<"
                                                case ThresholdComparator.GreaterThanOrEqual:
                                                    return ">="
                                                case ThresholdComparator.LessThanOrEqual:
                                                    return "<="
                                            }
                                        })(),
                                        type: "background",
                                        color: "#F44336"
                                    }
                                ]
                            }
                            metricSpecifiers={typedAlert.filters.metricSpecifiers}
                            formulas={typedAlert.filters.formula ? [typedAlert.filters.formula!] : []}
                            metoroQLQueries={typedAlert.filters.metoroQlQuery ? [typedAlert.filters.metoroQlQuery] : []}
                            reduceOption={(typedAlert.monitorEvaluation.monitorEvalutionPayload as AggregateMonitorEvaluationPayload).evaluationFunction as ReduceOption}
                            precision={2}
                            startTime={(() => {
                                let monitorEvalutionPayload = typedAlert.monitorEvaluation.monitorEvalutionPayload as AggregateMonitorEvaluationPayload;
                                return getAggregateStartEndTime(typedAlert.filters.metricSpecifiers, Math.floor(timeRange.getStartEnd()[1].getTime() / 1000) - monitorEvalutionPayload.window * getWindowUnitSeconds(monitorEvalutionPayload.windowUnit), Math.floor(timeRange.getStartEnd()[1].getTime() / 1000))[0]
                            })()}
                            endTime={(() => {
                                let monitorEvalutionPayload = typedAlert.monitorEvaluation.monitorEvalutionPayload as AggregateMonitorEvaluationPayload;
                                return getAggregateStartEndTime(typedAlert.filters.metricSpecifiers, Math.floor(timeRange.getStartEnd()[1].getTime() / 1000) - monitorEvalutionPayload.window * getWindowUnitSeconds(monitorEvalutionPayload.windowUnit), Math.floor(timeRange.getStartEnd()[1].getTime() / 1000))[1]
                            })()}
                        />
                    </div>
                    <MultiMetoroMetricsChart
                        className={"flex flex-grow"}
                        startTime={Math.floor(timeRange.getStartEnd()[0].getTime() / 1000)}
                        endTime={Math.floor(timeRange.getStartEnd()[1].getTime() / 1000)}
                        metricSpecifiers={alert.multiMetricAlert.filters.metricSpecifiers}
                        formulas={alert.multiMetricAlert.filters.formula ? [alert.multiMetricAlert.filters.formula] : []}
                        metoroQLQueries={alert.multiMetricAlert.filters.metoroQlQuery ? [alert.multiMetricAlert.filters.metoroQlQuery] : []}
                        type={ChartType.Line}
                        thresholdLabel={annotationProps.annotationLabel}
                        timePeriodHighlight={(() => {
                            let overrideStartTime = undefined;
                            let overrideEndTime = undefined;
                            if (typedAlert.monitorEvaluation.monitorEvaluationType == EvalType.Aggregate) {
                                if (typedAlert.filters.metricSpecifiers.length > 0) {
                                let monitorEvalutionPayload = typedAlert.monitorEvaluation.monitorEvalutionPayload as AggregateMonitorEvaluationPayload;
                                let startEnd = getAggregateStartEndTime(alert.multiMetricAlert.filters.metricSpecifiers, Math.floor(timeRange.getStartEnd()[1].getTime() / 1000)  - monitorEvalutionPayload.window * getWindowUnitSeconds(monitorEvalutionPayload.windowUnit), Math.floor(timeRange.getStartEnd()[1].getTime() / 1000))
                                let bucketSize = alert.multiMetricAlert.filters.metricSpecifiers[0].bucketSize!
                                    overrideStartTime = round(startEnd[0], bucketSize)
                                    // This subtraction is so that the points line up
                                    overrideEndTime = round(startEnd[1], bucketSize) - bucketSize
                                }
                                if (typedAlert.filters.metoroQlQuery) {
                                    let monitorEvalutionPayload = typedAlert.monitorEvaluation.monitorEvalutionPayload as AggregateMonitorEvaluationPayload;
                                    let metoroQLQuery = typedAlert.filters.metoroQlQuery;
                                    let startEnd = getMetoroQLQueryStartEndTime(metoroQLQuery, Math.floor(timeRange.getStartEnd()[1].getTime() / 1000) - monitorEvalutionPayload.window * getWindowUnitSeconds(monitorEvalutionPayload.windowUnit), Math.floor(timeRange.getStartEnd()[1].getTime() / 1000))
                                    overrideStartTime = startEnd[0]
                                    overrideEndTime = startEnd[1]
                                }
                            }
                            return getGraphAnnotationProperties(extractMonitorEvaluationObj(alert), timeRange, parseFloat(alert.multiMetricAlert.alarmCondition.threshold), alert.multiMetricAlert.alarmCondition.condition, typedAlert.filters.metricSpecifiers.length > 0 ? typedAlert.filters.metricSpecifiers[0].bucketSize : undefined,
                                overrideStartTime,
                                overrideEndTime).timePeriodToHighlight
                        })()}
                        hideLegend={false}
                    />
                </div>

            }
            {alert && alert.type == "MultiMetric" && alert.multiMetricAlert && typedAlert && typedAlert.monitorEvaluation.monitorEvaluationType != EvalType.Aggregate &&
                <div className={"flex flex-col"}>
                    <div className={"flex relative h-[300px]"}>
                        <MultiMetoroMetricsChart
                            className={"flex flex-grow"}
                            startTime={Math.floor(timeRange.getStartEnd()[0].getTime() / 1000)}
                            endTime={Math.floor(timeRange.getStartEnd()[1].getTime() / 1000)}
                            metricSpecifiers={alert.multiMetricAlert.filters.metricSpecifiers}
                            formulas={alert.multiMetricAlert.filters.formula ? [alert.multiMetricAlert.filters.formula] : []}
                            metoroQLQueries={alert.multiMetricAlert.filters.metoroQlQuery ? [alert.multiMetricAlert.filters.metoroQlQuery] : []}
                            type={ChartType.Line}
                            title={alert.name}
                            threshold={getGraphAnnotationProperties(extractMonitorEvaluationObj(alert), timeRange, parseFloat(alert.multiMetricAlert.alarmCondition.threshold), alert.multiMetricAlert.alarmCondition.condition).thresholdToSet}
                            thresholdLabel={annotationProps.annotationLabel}
                            timePeriodHighlight={getGraphAnnotationProperties(extractMonitorEvaluationObj(alert), timeRange, parseFloat(alert.multiMetricAlert.alarmCondition.threshold), alert.multiMetricAlert.alarmCondition.condition).timePeriodToHighlight}
                            hideLegend={false}
                        />
                    </div>
                </div>}
            {alert && doesNotHavePermission &&
            <div className={"text-textmedium border flex flex-none flex-col h-[128px] bg-backgroundmedium"}>
                <div className={"text-lg font-semibold p-4"}>
                    Firing History
                </div>
                <div className={"px-2"}>
                    <GanttChart
                        data={alertFiresToGanttData(alertFires, timeRange.getStartEnd()[0], timeRange.getStartEnd()[1])}/>
                </div>

            </div>
            }
            {alert && !doesNotHavePermission &&
                <div className={"text-textmedium flex grow shrink bg-backgroundmedium min-h-[400px] min-w-0"}>
                    <AlertFiringHistoryList alertFires={alertFires}
                                            alert={alert}
                                            alertType={alert.type}
                                            evaluationType={alertEvaluationType}
                                            evaluationFunction={alertEvaluationFunction}/>
                </div>}
        </div>
    </BaseView>
}

export {
    Alert
}